import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";

import { PageContainer } from "../components/PageContainer";
import { AlertMessage } from "../components/AlertMessage";
import { jobSchema } from "../helpers/formValidation";
import { IJobCreate } from "../interfaces/job/iJobCreate.interface";
import { jobService } from "../services/job.service";
import { useApi } from "../api/useApi";
import { IDropdownOption } from "../interfaces/iDropdown.interface";
import { FormSelect } from "../components/form/FormSelect";
import { FormGrid } from "../components/form/FormGrid";
import { FormField } from "../components/form/FormField";
import { FormErrors } from "../components/form/FormErrors";
import { useLocationCascade } from "../hooks/useLocationCascade";

import {
  btnPrimary,
  btnSecondary,
  formInputClasses,
  formTextAreaClasses,
} from "../styles/formClasses";
import {
  centeredPageHeaderClasses,
  pageHeadingClasses,
  pageLeadClasses,
  sectionCardClasses,
  subtleEyebrowClasses,
} from "../styles/pageClasses";
import { createJobPageClasses } from "../styles/pageSpecificClasses";

const defaultValues: IJobCreate = {
  id: 0,
  title: "",
  jobCategoryId: 0,
  locationRegionId: 0,
  locationId: 0,
  description: "",
  companyName: "",
  email: "",
  phone: null,
  effectiveFrom: "",
  salary: null,
};

export default function CreateJob() {
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { request } = useApi();

  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<IJobCreate>({
    resolver: zodResolver(jobSchema),
    defaultValues,
  });

  const selectedRegionId = watch("locationRegionId");
  const { categories, regions, locations } = useLocationCascade(selectedRegionId, setValue);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await jobService.get(Number(id));
        if (cancelled) return;
        reset(data as unknown as IJobCreate);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, reset]);

  async function onSubmit(form: IJobCreate) {
    setSubmitError(null);
    try {
      if (form.id) {
        await jobService.put(form, request);
      } else {
        await jobService.post(form, request);
      }
      navigate("");
    } catch (e) {
      setSubmitError("Failed to save job. Please try again.");
    }
  }

  if (loading) {
    return (
      <div className={createJobPageClasses.loading}>
        Loading job details...
      </div>
    );
  }

  return (
    <PageContainer maxWidth="4xl">
      <div className={centeredPageHeaderClasses}>
        <span className={subtleEyebrowClasses}>{id ? "Edit Listing" : "New Listing"}</span>
        <h1 className={`${createJobPageClasses.heading} ${pageHeadingClasses}`}>
          {id ? "Edit Job Posting" : "Post a New Job in Greece"}
        </h1>
        <p className={pageLeadClasses}>
          Fill out the form below to share a new summer job opportunity.
        </p>
      </div>

      <div className={sectionCardClasses}>
        <form onSubmit={handleSubmit(onSubmit)} className={createJobPageClasses.form}>
          <FormErrors errors={errors} />

          <FormGrid>
            <FormField label="Company Name" error={errors.companyName?.message}>
              <input
                {...register("companyName")}
                className={formInputClasses}
                placeholder="e.g. Aegean Hotels"
              />
            </FormField>

            <FormField label="Job Title" error={errors.title?.message}>
              <input
                {...register("title")}
                className={formInputClasses}
                placeholder="e.g. Beach Bar Staff, Hotel Receptionist"
              />
            </FormField>
          </FormGrid>

          <FormField label="Job Description" error={errors.description?.message}>
            <textarea
              {...register("description")}
              className={formTextAreaClasses}
              placeholder="Responsibilities, requirements, perks, and working conditions."
            />
          </FormField>

          <FormGrid>
            <FormField label="Region" error={errors.locationRegionId?.message}>
              <FormSelect<IDropdownOption, IJobCreate>
                name="locationRegionId"
                control={control}
                options={regions}
                placeholder="Select a region"
              />
            </FormField>

            <FormField label="Location" error={errors.locationId?.message}>
              <FormSelect<IDropdownOption, IJobCreate>
                name="locationId"
                control={control}
                options={locations}
                placeholder="Select location"
                disabled={!selectedRegionId || selectedRegionId === 0}
              />
            </FormField>
          </FormGrid>

          <FormGrid>
            <FormField label="Job Type" error={errors.jobCategoryId?.message}>
              <FormSelect<IDropdownOption, IJobCreate>
                name="jobCategoryId"
                control={control}
                options={categories}
                placeholder="Select job type"
              />
            </FormField>

            <FormField label="Effective From" error={errors.effectiveFrom?.message}>
              <input
                type="date"
                {...register("effectiveFrom")}
                className={`${formInputClasses} ${createJobPageClasses.dateInput}`}
              />
            </FormField>
          </FormGrid>

          <FormGrid>
            <FormField label="Salary (EUR per month)" error={""}>
              <input
                type="number"
                {...register("salary", { valueAsNumber: true })}
                placeholder="Optional"
                className={formInputClasses}
              />
            </FormField>

            <FormField label="Phone Number" error={errors.phone?.message}>
              <input
                type="tel"
                {...register("phone")}
                placeholder="e.g. +306912345678"
                className={formInputClasses}
              />
            </FormField>
          </FormGrid>

          <FormField label="Contact Email" error={errors.email?.message}>
            <input
              type="email"
              {...register("email")}
              placeholder="e.g. jobs@company.com"
              className={formInputClasses}
              autoComplete="email"
            />
          </FormField>

          <AlertMessage type="error" message={submitError} />

          <div className={createJobPageClasses.actions}>
            <button type="button" onClick={() => navigate(-1)} className={btnSecondary}>
              Cancel
            </button>
            <button type="submit" className={btnPrimary}>
              {id ? "Update Job" : "Post Job"}
            </button>
          </div>
        </form>
      </div>
    </PageContainer>
  );
}
